import { splitPublishedForTheme } from '../../helpers/intelligence/filterThemeRows'
import { driftReasons } from '../../helpers/intelligence/driftLabels'

/**
 * Eén publicatie als knop naar het inzicht. Alleen spans erin: een kop of
 * alinea in een knop verliest zijn rol.
 * AI-CONTEXT: See docs/pitfalls.md#ui-heading-inside-button-stops-being-a-heading
 *
 * @param {object} props
 * @param {object} props.insight - QuestionInsights-document
 * @param {(insightId: string) => void} props.onOpen
 */
const PublicationRow = ({ insight, onOpen }) => {
    // Naar het inzicht zelf, niet meer via een vraag.
    const rijen = (insight.Published && insight.Published.rows) || []
    const drift = insight.Drift && insight.Drift.needsReview ? insight.Drift : null
    return (
        <button type="button" className="intelligence-theme-row" onClick={() => onOpen(insight.ID)}>
            {/* De redactietekst is wat de lezer buiten ziet. Is hij leeg, dan
                zeggen we dát — niet niets. */}
            <span className="intelligence-theme-row__title">
                {insight.Text || 'Nog geen tekst geschreven bij dit inzicht.'}
            </span>
            <span className="intelligence-theme-row__meta">
                {drift && <span className="intelligence-badge intelligence-badge--review">Nalopen</span>}
                {drift && <span className="intelligence-theme-row__stand">{driftReasons(drift)}</span>}
                <span className="intelligence-theme-row__stand">
                    {rijen.length === 1 ? '1 regel' : `${rijen.length} regels`}
                </span>
            </span>
        </button>
    )
}

/**
 * Publicaties: wat er onder dit thema daadwerkelijk buiten staat, mét de
 * markering van de nachtelijke controle. Alleen eigen publicaties (met dit
 * ThemeID); effectbrede staan hier niet (besluit user 2026-09-14).
 *
 * Drie toestanden uit elkaar: niet kunnen lezen, nog niet weten, en echt leeg.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {object} props
 * @param {object} props.theme - Het thema
 * @param {{insights: Array<object>, loaded: boolean, error: (Error|null)}} props.published - Uit usePublishedInsights
 * @param {(insightId: string) => void} props.onOpen - Naar de detailpagina van het inzicht
 * @param {boolean} [props.embedded] - Als tabblad binnen de inzichtensectie: geen eigen kop
 */
const ThemePublicationsSection = ({ theme, published, onOpen, embedded = false }) => {
    const inhoud = () => {
        if (published.error) {
            return (
                <p className="intelligence-state intelligence-state--error">
                    De publicaties konden niet worden geladen: {published.error.message}
                </p>
            )
        }
        if (!published.loaded) return <p className="intelligence-state">Publicaties laden…</p>

        const { own } = splitPublishedForTheme(theme, published.insights)
        if (own.length === 0) return <p className="intelligence-state">Onder dit thema is nog niets gepubliceerd.</p>
        return (
            <div className="intelligence-theme-list">
                {own.map((insight) => <PublicationRow key={insight.docid} insight={insight} onOpen={onOpen} />)}
            </div>
        )
    }

    const uitleg = (
        <p className={embedded ? 'intelligence-agenda__hint' : 'intelligence-panel__hint'}>
            Wat er onder dit thema op de marketingsite staat. 'Nalopen' betekent dat de
            nachtelijke controle een verschil zag tussen de tekst en de cijfers van nu.
        </p>
    )

    if (embedded) return <>{uitleg}{inhoud()}</>

    return (
        <section className="intelligence-panel">
            <h3 className="intelligence-panel__title">Publicaties</h3>
            {uitleg}
            {inhoud()}
        </section>
    )
}

export default ThemePublicationsSection
